import { useCallback, useRef, useState } from 'react'
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'
import FmdGoodOutlined from '@mui/icons-material/FmdGoodOutlined'
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined'
import Avatar from '@mui/material/Avatar'
import { useMediaQuery, useTheme } from '@mui/material'
import { useFormatMessage } from '@/translations/useFormatMessage'
import type { LocationData, MenuSchedule } from '@/shared/api/location'
import { useAppDispatch, useAppSelector } from '@/store'
import { sizing, colors } from '@/styles/designTokens'
import {
	selectDineInTableName,
	selectSelectedMenuSchedule,
	selectSelectedOrderTypeGuid,
	setMenuSchedule,
} from '@/store/ticketSlice'
import { SignInModal } from '@/components/auth/SignInModal'
import { AccountPopover } from '@/components/auth/AccountPopover'
import { TablePill } from './TablePill'
import { MenuSchedulePill } from './MenuSchedulePill'
import {
	HeaderRoot,
	ExpandedOuter,
	ExpandedContent,
	HeaderRow,
	BrandingBox,
	LocationName,
	LocationAddress,
	SchedulePillDesktopWrap,
	SchedulePillMobileWrap,
	SignInButton,
	SignInButtonText,
	SignInIconButton,
	AccountIconButton,
	CompactOuter,
	CompactContent,
} from './MenuHeader.styled'

function buildAddressLine(location: LocationData): string {
	let address = ''

	if (location.addressSelection === 'business') {
		const streetCity = [location.addressLine1, location.addressLine2, location.city].filter(
			Boolean,
		)
		const stateZip = [location.state, location.zip].filter(Boolean).join(' ')
		if (stateZip) streetCity.push(stateZip)
		address = streetCity.join(', ')
	} else if (location.addressSelection === 'custom') {
		address = location.customAddress ?? ''
	}

	const phone = location.oloContactPhone ?? ''
	if (address && phone) return `${address} | ${phone}`
	return address || phone
}

interface MenuHeaderProps {
	location?: LocationData
	schedules?: MenuSchedule[]
	isCompact?: boolean
	hideSignIn?: boolean
}

export default function MenuHeader({
	location,
	schedules,
	isCompact = false,
	hideSignIn = false,
}: MenuHeaderProps) {
	const dispatch = useAppDispatch()
	const { t } = useFormatMessage()
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))
	const [isSignInModalOpen, setIsSignInModalOpen] = useState(false)
	const [accountPopoverAnchorEl, setAccountPopoverAnchorEl] = useState<HTMLElement | null>(null)
	const popoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const isOnAvatarRef = useRef(false)

	const selectedMenuSchedule = useAppSelector(selectSelectedMenuSchedule)
	const dineInTableName = useAppSelector(selectDineInTableName)
	const selectedOrderTypeGuid = useAppSelector(selectSelectedOrderTypeGuid)
	const isDineIn = selectedOrderTypeGuid === 'DINE_IN'
	const authUser = useAppSelector((state) => state.auth.user)

	// Never fall back to location.menuSchedules — may be stale and include off-hours schedules (STO-3811).
	const pillSchedules = schedules ?? []
	const activeSchedule =
		selectedMenuSchedule && pillSchedules.some((s) => s.ref === selectedMenuSchedule.ref)
			? selectedMenuSchedule
			: (pillSchedules[0] ?? null)

	// Cancel any pending close. Called whenever the pointer is over the
	// avatar or the popover (either directly or after crossing the gap).
	const cancelPopoverClose = useCallback(() => {
		if (popoverTimeoutRef.current) {
			clearTimeout(popoverTimeoutRef.current)
			popoverTimeoutRef.current = null
		}
	}, [])

	// Schedule a close after the grace period. Re-entering either element
	// cancels it, so the popover stays open across the avatar↔popover gap.
	const schedulePopoverClose = useCallback(() => {
		cancelPopoverClose()
		popoverTimeoutRef.current = setTimeout(() => {
			setAccountPopoverAnchorEl(null)
			popoverTimeoutRef.current = null
		}, 1000)
	}, [cancelPopoverClose])

	const handleAccountMouseEnter = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			isOnAvatarRef.current = true
			cancelPopoverClose()
			setAccountPopoverAnchorEl(e.currentTarget)
		},
		[cancelPopoverClose],
	)

	const handleAccountMouseLeave = useCallback(() => {
		isOnAvatarRef.current = false
		schedulePopoverClose()
	}, [schedulePopoverClose])

	// On touch/mobile (≤720px) there is no reliable hover, so the avatar opens
	// the account popover on click instead. Toggles so a second tap closes it.
	// Capture currentTarget before the state updater runs — React nullifies the
	// synthetic event's currentTarget once the handler returns, so reading it
	// inside the updater callback would yield null on the first tap.
	const handleAccountClick = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			const target = e.currentTarget
			cancelPopoverClose()
			setAccountPopoverAnchorEl((prev) => (prev ? null : target))
		},
		[cancelPopoverClose],
	)

	// Immediate close for the mobile modal (backdrop / close button) — no hover
	// grace period applies there.
	const handleAccountClose = useCallback(() => {
		cancelPopoverClose()
		setAccountPopoverAnchorEl(null)
	}, [cancelPopoverClose])

	const handlePopoverMouseEnter = cancelPopoverClose

	const handlePopoverMouseLeave = schedulePopoverClose

	const addressLine = location ? buildAddressLine(location) : ''

	const handleScheduleSelect = useCallback(
		(s: MenuSchedule) => dispatch(setMenuSchedule(s)),
		[dispatch],
	)

	const handleSignIn = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			if (authUser) {
				handleAccountMouseEnter(e)
			} else if (location?.locationRef) {
				setIsSignInModalOpen(true)
			}
		},
		[authUser, location, handleAccountMouseEnter],
	)

	const handleCloseSignInModal = useCallback(() => {
		setIsSignInModalOpen(false)
	}, [])

	const userInitials = (() => {
		if (!authUser?.firstName || !authUser?.lastName) return ''
		const firstName = authUser.firstName.charAt(0).toUpperCase()
		const lastName = authUser.lastName.charAt(0).toUpperCase()
		const initials = `${firstName}${lastName}`
		return initials
	})()

	if (!location) return null

	return (
		<>
			<SignInModal
				isOpen={isSignInModalOpen}
				onClose={handleCloseSignInModal}
				locationRef={location.locationRef}
				locationId={location.id}
				locationCountryCode={location.countryCode}
			/>
			<AccountPopover
				isOpen={Boolean(accountPopoverAnchorEl)}
				anchorEl={accountPopoverAnchorEl}
				onClose={isMobile ? handleAccountClose : handleAccountMouseLeave}
				onMouseEnter={handlePopoverMouseEnter}
				onMouseLeave={handlePopoverMouseLeave}
			/>
			<HeaderRoot data-testid="menu-header">
				{/* ── Compact row (scrolled state): logo + schedule inline, no pill styling ── */}
				<CompactOuter isCompact={isCompact}>
					<CompactContent isCompact={isCompact}>
						<Avatar
							src={location.logoUrl ?? location.oloLogoUrl ?? undefined}
							alt={location.name ?? ''}
							sx={{
								flexShrink: 0,
								width: sizing.avatarCompact,
								height: sizing.avatarCompact,
							}}
						>
							{!(location.logoUrl ?? location.oloLogoUrl) && <FmdGoodOutlined />}
						</Avatar>

						{activeSchedule && (
							<MenuSchedulePill
								schedule={activeSchedule}
								schedules={pillSchedules}
								onSelect={handleScheduleSelect}
								isDineIn={isDineIn}
								compact
							/>
						)}

						{dineInTableName && <TablePill />}
					</CompactContent>
				</CompactOuter>

				{/* ── Expanded content (default state) ── */}
				<ExpandedOuter isCompact={isCompact}>
					<ExpandedContent isCompact={isCompact}>
						{/* Row 1: branding + right actions */}
						<HeaderRow>
							<Avatar
								src={location.logoUrl ?? location.oloLogoUrl ?? undefined}
								alt={location.name ?? ''}
								sx={{
									flexShrink: 0,
									width: { xs: sizing.avatarMobile, lg: sizing.avatarDesktop },
									height: { xs: sizing.avatarMobile, lg: sizing.avatarDesktop },
								}}
							>
								{!(location.logoUrl ?? location.oloLogoUrl) && <FmdGoodOutlined />}
							</Avatar>

							<BrandingBox hasSignIn={location.signInPromptEnabled}>
								<LocationName>{location.name}</LocationName>
								<LocationAddress
									data-testid="location-address"
									title={addressLine || undefined}
								>
									{addressLine}
								</LocationAddress>
							</BrandingBox>

							{activeSchedule && (
								<SchedulePillDesktopWrap>
									<MenuSchedulePill
										schedule={activeSchedule}
										schedules={pillSchedules}
										onSelect={handleScheduleSelect}
										isDineIn={isDineIn}
									/>
								</SchedulePillDesktopWrap>
							)}

							{dineInTableName && <TablePill />}

							{authUser ? (
								<AccountIconButton
									onMouseEnter={isMobile ? undefined : handleAccountMouseEnter}
									onMouseLeave={isMobile ? undefined : handleAccountMouseLeave}
									onClick={
										isMobile ? handleAccountClick : handleAccountMouseEnter
									}
									data-testid="sign-in-avatar"
									aria-label={t('component:menuHeader:account:label')}
								>
									<Avatar
										sx={{
											width: '100%',
											height: '100%',
										}}
									>
										{userInitials || (
											<HowToRegOutlinedIcon
												sx={{
													fontSize: sizing.iconSm,
													color: colors.textSecondary,
												}}
											/>
										)}
									</Avatar>
								</AccountIconButton>
							) : hideSignIn ? null : (
								<>
									<SignInButton
										data-testid="sign-in-pill"
										onClick={handleSignIn}
										aria-label={t('component:menuHeader:signIn:label')}
									>
										<PersonOutlinedIcon sx={{ fontSize: sizing.navIcon }} />
										<SignInButtonText>
											{t('component:menuHeader:signIn:label')}
										</SignInButtonText>
									</SignInButton>
									<SignInIconButton
										data-testid="sign-in-icon-button"
										onClick={handleSignIn}
										aria-label={t('component:menuHeader:signIn:label')}
									>
										<PersonOutlinedIcon sx={{ fontSize: sizing.navIcon }} />
									</SignInIconButton>
								</>
							)}
						</HeaderRow>

						{/* Row 2 (tablet/mobile only): full-width schedule pill */}
						{activeSchedule && (
							<SchedulePillMobileWrap>
								<MenuSchedulePill
									schedule={activeSchedule}
									schedules={pillSchedules}
									onSelect={handleScheduleSelect}
									isDineIn={isDineIn}
									fullWidth
								/>
							</SchedulePillMobileWrap>
						)}
					</ExpandedContent>
				</ExpandedOuter>
			</HeaderRoot>
		</>
	)
}
